// frontend/src/contexts/LabReportUploadContext.tsx
import { createContext, useContext, useState, ReactNode, useCallback } from 'react';

export interface UploadedFile {
  id: string;
  name: string;
  size: number;
  type: string;
  uploadedAt: Date;
  status: 'uploading' | 'processing' | 'completed' | 'error';
  progress: number;
  extractedData?: any;
  report_id?: string;
  lab_summary?: string;
  mode?: string;
}

interface LabReportUploadContextType {
  files: UploadedFile[];
  addFile: (file: UploadedFile) => void;
  updateFile: (fileId: string, updates: Partial<UploadedFile>) => void;
  removeFile: (fileId: string) => void;
  clearFiles: () => void;
}

const LabReportUploadContext = createContext<LabReportUploadContextType | undefined>(undefined);

export function LabReportUploadProvider({ children }: { children: ReactNode }) {
  const [files, setFiles] = useState<UploadedFile[]>([]);

  const addFile = useCallback((file: UploadedFile) => {
    setFiles((prev) => [...prev, file]);
  }, []);

  const updateFile = useCallback((fileId: string, updates: Partial<UploadedFile>) => {
    setFiles((prev) =>
      prev.map((f) =>
        f.id === fileId
          ? { ...f, ...updates }
          : f
      )
    );
  }, []);

  const removeFile = useCallback((fileId: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== fileId));
  }, []);

  const clearFiles = useCallback(() => {
    setFiles([]);
  }, []);

  return (
    <LabReportUploadContext.Provider
      value={{
        files,
        addFile,
        updateFile,
        removeFile,
        clearFiles,
      }}
    >
      {children}
    </LabReportUploadContext.Provider>
  );
}

export function useLabReportUpload() {
  const context = useContext(LabReportUploadContext);
  if (context === undefined) {
    throw new Error('useLabReportUpload must be used within a LabReportUploadProvider');
  }
  return context;
}
